import React from 'react';
import { HStack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

interface LanguageSwitcherProps {
  variant?: 'header' | 'footer';
  onLanguageChange?: () => void;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'header', onLanguageChange }) => {
  const { i18n } = useTranslation('navigation');
  const navigate = useNavigate();
  const location = useLocation();

  const changeLanguage = (lng: string) => {
    const currentPath = location.pathname;

    // Remove current language prefix if it exists
    let pathWithoutLang = currentPath.replace(/^\/(en|es)/, '') || '/';

    // Ensure path starts with a slash
    if (!pathWithoutLang.startsWith('/')) {
      pathWithoutLang = `/${pathWithoutLang}`;
    }

    // Add new language prefix
    const newPath = `/${lng}${pathWithoutLang}`;

    i18n.changeLanguage(lng);
    navigate(newPath);

    // Close mobile menu if callback provided
    if (onLanguageChange) {
      onLanguageChange();
    }
  };

  const currentLanguage = i18n.language || 'en';

  if (variant === 'footer') {
    return (
      <HStack spacing={2} justify="center">
        <Text
          fontSize="sm"
          color={currentLanguage === 'en' ? 'black' : 'gray.600'}
          fontFamily="Source Code Pro"
          cursor={currentLanguage === 'en' ? 'default' : 'pointer'}
          fontWeight={currentLanguage === 'en' ? 'bold' : 'normal'}
          onClick={() => currentLanguage !== 'en' && changeLanguage('en')}
        >
          EN
        </Text>
        <Text fontSize="sm" color="gray.600" fontFamily="Source Code Pro">
          |
        </Text>
        <Text
          fontSize="sm"
          color={currentLanguage === 'es' ? 'black' : 'gray.600'}
          fontFamily="Source Code Pro"
          cursor={currentLanguage === 'es' ? 'default' : 'pointer'}
          fontWeight={currentLanguage === 'es' ? 'bold' : 'normal'}
          onClick={() => currentLanguage !== 'es' && changeLanguage('es')}
        >
          ES
        </Text>
      </HStack>
    );
  }

  return (
    <HStack
      spacing={1}
      border="2px solid black"
      borderRadius="md"
      px={2}
      py={1}
      fontFamily="Source Code Pro"
      fontSize="xs"
    >
      <Text
        color={currentLanguage === 'en' ? 'black' : 'gray.400'}
        fontWeight={currentLanguage === 'en' ? 'bold' : 'normal'}
        cursor={currentLanguage === 'en' ? 'default' : 'pointer'}
        onClick={() => currentLanguage !== 'en' && changeLanguage('en')}
        _hover={currentLanguage !== 'en' ? { color: 'gray.600' } : {}}
      >
        EN
      </Text>
      <Text color="gray.400">|</Text>
      <Text
        color={currentLanguage === 'es' ? 'black' : 'gray.400'}
        fontWeight={currentLanguage === 'es' ? 'bold' : 'normal'}
        cursor={currentLanguage === 'es' ? 'default' : 'pointer'}
        onClick={() => currentLanguage !== 'es' && changeLanguage('es')}
        _hover={currentLanguage !== 'es' ? { color: 'gray.600' } : {}}
      >
        ES
      </Text>
    </HStack>
  );
};

export default LanguageSwitcher;